import { History } from 'history';
import { ParsedQuery } from 'query-string';
import { getQueryParams, updatePageUrl } from 'helpers/queryString';

// Filter interface for checkpoint pages
export interface ICheckpointFilters {
  usersIds?: number[] | string[];
  jobsIds?: number[] | string[];
  severity?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  status?: string;
}

// Single localStorage key for all checkpoint filters
export const CHECKPOINT_FILTERS_STORAGE_KEY = 'checkpoint-filters';

// Define page identifiers for each checkpoint page
export const CHECKPOINT_PAGE_KEYS = {
  SCANS: 'scans',
  INCIDENTS: 'incidents',
  PARKING: 'parking',
} as const;

// Route to page key mapping
export const ROUTE_TO_PAGE_KEY = {
  'checkpoints/scans': CHECKPOINT_PAGE_KEYS.SCANS,
  'checkpoints/incidents': CHECKPOINT_PAGE_KEYS.INCIDENTS,
  'checkpoints/parking': CHECKPOINT_PAGE_KEYS.PARKING,
} as const;

// Type for the complete filter storage structure
export interface ICheckpointFiltersStorage {
  [CHECKPOINT_PAGE_KEYS.SCANS]?: ICheckpointFilters;
  [CHECKPOINT_PAGE_KEYS.INCIDENTS]?: ICheckpointFilters;
  [CHECKPOINT_PAGE_KEYS.PARKING]?: ICheckpointFilters;
}

/**
 * Get page key based on current route
 */
export const getPageKeyFromRoute = (pathname: string): string | null => {
  const routeKey = Object.keys(ROUTE_TO_PAGE_KEY).find(route => pathname.includes(route));

  if (routeKey) {
    return ROUTE_TO_PAGE_KEY[routeKey as keyof typeof ROUTE_TO_PAGE_KEY];
  }

  return null;
};

/**
 * Get all checkpoint filters from localStorage
 */
export const getAllCheckpointFilters = (): ICheckpointFiltersStorage => {
  try {
    const stored = localStorage.getItem(CHECKPOINT_FILTERS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to load checkpoint filters from localStorage:', error);
    return {};
  }
};

/**
 * Save filters to localStorage for a specific page
 */
export const saveFiltersToStorage = (pageKey: string, filters: ICheckpointFilters): void => {
  try {
    // Remove pagination params from storage (they should only be in URL)
    const { page, limit, ...filtersToStore } = filters;

    const allFilters = getAllCheckpointFilters();
    allFilters[pageKey as keyof ICheckpointFiltersStorage] = filtersToStore;

    localStorage.setItem(CHECKPOINT_FILTERS_STORAGE_KEY, JSON.stringify(allFilters));
  } catch (error) {
    console.warn('Failed to save filters to localStorage:', error);
  }
};

/**
 * Load filters from localStorage for a specific page
 */
export const loadFiltersFromStorage = (pageKey: string): ICheckpointFilters | null => {
  try {
    const allFilters = getAllCheckpointFilters();
    return allFilters[pageKey as keyof ICheckpointFiltersStorage] || null;
  } catch (error) {
    console.warn('Failed to load filters from localStorage:', error);
    return null;
  }
};

/**
 * Clear all checkpoint filters from localStorage
 */
export const clearAllCheckpointFilters = (): void => {
  try {
    localStorage.removeItem(CHECKPOINT_FILTERS_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear all checkpoint filters from localStorage:', error);
  }
};

/**
 * Merge URL params with localStorage filters
 * URL params take precedence over localStorage
 */
export const mergeFiltersWithStorage = (
  urlParams: ParsedQuery,
  storageFilters: ICheckpointFilters | null,
): ICheckpointFilters => {
  const baseFilters = storageFilters || {};

  // Convert URL params to proper types
  const mergedFilters: ICheckpointFilters = {
    ...baseFilters,
    ...urlParams,
  };

  // Handle array conversion for usersIds and jobsIds
  if (urlParams.usersIds) {
    mergedFilters.usersIds = Array.isArray(urlParams.usersIds)
      ? urlParams.usersIds.map(id => Number(id))
      : [Number(urlParams.usersIds)];
  }

  if (urlParams.jobsIds) {
    mergedFilters.jobsIds = Array.isArray(urlParams.jobsIds)
      ? urlParams.jobsIds.map(id => Number(id))
      : [Number(urlParams.jobsIds)];
  }

  // Handle pagination
  if (urlParams.page) {
    mergedFilters.page = Number(urlParams.page);
  }

  if (urlParams.limit) {
    mergedFilters.limit = Number(urlParams.limit);
  }

  return mergedFilters;
};

/**
 * Update both URL and localStorage with new filter values
 */
export const updateCheckpointFilters = (
  pathname: string,
  currentSearch: string,
  newFilters: Partial<ICheckpointFilters>,
  history: History,
): void => {
  const pageKey = getPageKeyFromRoute(pathname);

  if (!pageKey) {
    console.warn('No page key found for pathname:', pathname);
    return;
  }

  // Update URL
  const prevQueryParams = getQueryParams(currentSearch, {});
  const newQueryParams = { ...newFilters, page: 1 }; // Reset to page 1 when filters change
  updatePageUrl(prevQueryParams, newQueryParams, history);

  // Update localStorage with the new filters
  const currentStorageFilters = loadFiltersFromStorage(pageKey) || {};
  const updatedFilters = { ...currentStorageFilters, ...newFilters };
  saveFiltersToStorage(pageKey, updatedFilters);
};

/**
 * Initialize filters on page load
 * Combines URL params with localStorage, giving precedence to URL
 */
export const initializeCheckpointFilters = (
  pathname: string,
  search: string,
  history: History,
  defaultPage: number = 1,
  defaultLimit?: number,
): ICheckpointFilters => {
  const pageKey = getPageKeyFromRoute(pathname);

  if (!pageKey) {
    return getQueryParams(search, {}) as ICheckpointFilters;
  }

  const urlParams = getQueryParams(search, {});
  const storageFilters = loadFiltersFromStorage(pageKey);

  // If URL has no filters but storage does, update URL with storage filters
  const hasUrlFilters = Object.keys(urlParams).some(key => key !== 'page' && key !== 'limit');

  if (!hasUrlFilters && storageFilters && Object.keys(storageFilters).length > 0) {
    const filtersWithPagination = {
      ...storageFilters,
      page: defaultPage,
      ...(defaultLimit && { limit: defaultLimit }),
    };

    // Update URL with storage filters
    updatePageUrl({}, filtersWithPagination, history);
    return filtersWithPagination;
  }

  // Merge URL params with storage, giving precedence to URL
  return mergeFiltersWithStorage(urlParams, storageFilters);
};
